import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import Dialog from "../../components/ui/Dialog";
import {
  useCreateDtrExport,
  useDtrExportCheck,
  useResolveDtrExport,
} from "../../api/queries/approvalQueries";
import { getApiErrorMessage } from "../../utils/apiError";
import { formatPlainDate, formatTime } from "../../utils/dateTime";
import { Link } from "react-router-dom";
import { CorrectionDecision } from "./DtrTab";

const EMPTY = [];
const ISSUE_LABEL = {
  MISSING_CHECKOUT: "No check-out",
  NO_RECORD: "No record",
  PENDING_CORRECTION: "Correction waiting",
  PENDING_OVERTIME: "Overtime waiting",
};
const keyOf = (employeeId, date) => `${employeeId}|${date}`;

/** A fix the user set for one day is complete enough to send. */
const isComplete = (fix) =>
  fix &&
  (fix.action === "SET_CHECKOUT"
    ? !!fix.checkOutTime
    : fix.action === "ADD_TIMES"
      ? !!fix.checkInTime &&
        !!fix.checkOutTime &&
        fix.checkOutTime > fix.checkInTime
      : !!fix.action);

/** One day to settle: the problem, then the choices the backend allows. */
const IssueRow = ({ group, issue, fix, onFix, employee }) => {
  const set = (changes) => onFix({ ...(fix || {}), ...changes });
  const action = fix?.action || "";
  return (
    <tr>
      <td className="text-nowrap">
        {formatPlainDate(issue.date, "ddd, MMM D")}
      </td>
      <td className="text-nowrap">
        <span className="status-pill status-pill--amber">
          {ISSUE_LABEL[issue.type] || issue.type}
        </span>
        {issue.firstCheckIn && (
          <div className="fs-070 text-slate-500 mt-1">
            In {formatTime(issue.firstCheckIn)}
          </div>
        )}
      </td>
      <td>
        {issue.type === "PENDING_CORRECTION" && issue.requestId && (
          <CorrectionDecision
            correction={{ id: issue.requestId }}
            employeeName={employee.name}
            date={issue.date}
          />
        )}
        {issue.type === "PENDING_OVERTIME" && (
          <Link
            className="btn btn-sm btn-light border"
            to={`/time?tab=overtime&search=${encodeURIComponent(
              employee.employeeNumber || employee.name,
            )}`}
            target="_blank"
            rel="noreferrer"
          >
            <i className="bi bi-box-arrow-up-right me-1"></i>Decide in Overtime
          </Link>
        )}
        <div className="d-flex flex-wrap align-items-center gap-2">
          {issue.actions.includes("SET_CHECKOUT") && (
            <label className="d-inline-flex align-items-center gap-1 fs-080 m-0">
              <input
                type="radio"
                name={group}
                checked={action === "SET_CHECKOUT"}
                onChange={() => set({ action: "SET_CHECKOUT" })}
              />
              Check-out
              <input
                type="time"
                className="form-control form-control-sm w-auto"
                value={fix?.checkOutTime || ""}
                onChange={(e) =>
                  set({ action: "SET_CHECKOUT", checkOutTime: e.target.value })
                }
                aria-label="Check-out time"
              />
            </label>
          )}
          {issue.actions.includes("ADD_TIMES") && (
            <label className="d-inline-flex align-items-center gap-1 fs-080 m-0">
              <input
                type="radio"
                name={group}
                checked={action === "ADD_TIMES"}
                onChange={() => set({ action: "ADD_TIMES" })}
              />
              Times
              <input
                type="time"
                className="form-control form-control-sm w-auto"
                value={fix?.checkInTime || ""}
                onChange={(e) =>
                  set({ action: "ADD_TIMES", checkInTime: e.target.value })
                }
                aria-label="Check-in time"
              />
              –
              <input
                type="time"
                className="form-control form-control-sm w-auto"
                value={fix?.checkOutTime || ""}
                onChange={(e) =>
                  set({ action: "ADD_TIMES", checkOutTime: e.target.value })
                }
                aria-label="Check-out time"
              />
            </label>
          )}
          {issue.actions.includes("MARK_HALF_DAY") && (
            <label className="d-inline-flex align-items-center gap-1 fs-080 m-0">
              <input
                type="radio"
                name={group}
                checked={action === "MARK_HALF_DAY"}
                onChange={() => set({ action: "MARK_HALF_DAY" })}
              />
              Half day
            </label>
          )}
          {issue.actions.includes("MARK_ABSENT") && (
            <label className="d-inline-flex align-items-center gap-1 fs-080 m-0">
              <input
                type="radio"
                name={group}
                checked={action === "MARK_ABSENT"}
                onChange={() => set({ action: "MARK_ABSENT" })}
              />
              Absent
            </label>
          )}
          {action && (
            <button
              type="button"
              className="btn btn-sm btn-link p-0 text-slate-500"
              onClick={() => onFix(null)}
            >
              Clear
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

/**
 * Export → before the export: who goes (time entries or paid leave), who is
 * skipped, and the days to settle first (missing check-out, past day with no
 * record, a correction or overtime still waiting). HR fixes
 * them here (the normal attendance edits), marks them absent, or excludes the
 * employee (left for a later export). Exporting approves everyone it sends.
 */
const ExportCheckDialog = ({ period, scope, label, onClose, onExported }) => {
  // Excluded employees: { id → employee } (the check no longer returns them)
  const [excluded, setExcluded] = useState({});
  // Draft fixes: { "employeeId|date" → { action, checkInTime?, checkOutTime? } }
  const [fixes, setFixes] = useState({});
  const [failed, setFailed] = useState([]);
  const [showInfo, setShowInfo] = useState(false);

  const excludeIds = useMemo(() => Object.keys(excluded).sort(), [excluded]);
  const body = useMemo(
    () => ({ ...period, ...scope, excludeEmployeeIds: excludeIds }),
    [period, scope, excludeIds],
  );
  const check = useDtrExportCheck(body);
  const resolve = useResolveDtrExport();
  const create = useCreateDtrExport();
  const data = check.data;
  const withIssues = data?.withIssues || EMPTY;
  const paidLeave = data?.includedForPaidLeave || EMPTY;
  const skipped = data?.skipped || EMPTY;
  const busy = resolve.isPending || create.isPending;

  const draftList = Object.entries(fixes).filter(([, f]) => isComplete(f));
  const incomplete = Object.values(fixes).filter(
    (f) => f && !isComplete(f),
  ).length;

  const setFix = (employeeId, date, fix) =>
    setFixes((prev) => {
      const next = { ...prev };
      if (fix) next[keyOf(employeeId, date)] = fix;
      else delete next[keyOf(employeeId, date)];
      return next;
    });
  // Bulk: every no-record day (of one employee, or everyone) → absent
  const markNoRecordAbsent = (rows) =>
    setFixes((prev) => {
      const next = { ...prev };
      rows.forEach(({ employee, issues }) =>
        issues
          .filter((i) => i.type === "NO_RECORD")
          .forEach((i) => {
            const k = keyOf(employee.id, i.date);
            if (!next[k]) next[k] = { action: "MARK_ABSENT" };
          }),
      );
      return next;
    });
  const exclude = (employee) => {
    setExcluded((prev) => ({ ...prev, [employee.id]: employee }));
    setFixes((prev) =>
      Object.fromEntries(
        Object.entries(prev).filter(([k]) => !k.startsWith(`${employee.id}|`)),
      ),
    );
  };
  const include = (id) =>
    setExcluded((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });

  const applyFixes = () =>
    resolve.mutate(
      {
        year: period.year,
        month: period.month,
        cutoff: period.cutoff,
        companyId: scope.companyId,
        fixes: draftList.map(([k, f]) => {
          const [employeeId, date] = k.split("|");
          return {
            employeeId,
            date,
            action: f.action,
            checkInTime: f.action === "ADD_TIMES" ? f.checkInTime : undefined,
            checkOutTime:
              f.action === "SET_CHECKOUT" || f.action === "ADD_TIMES"
                ? f.checkOutTime
                : undefined,
            remarks: "Fixed before payroll export",
          };
        }),
      },
      {
        onSuccess: (res) => {
          const bad = (res?.results || []).filter((r) => !r.ok);
          setFailed(bad);
          // keep only the fixes that failed, to correct them
          setFixes((prev) =>
            Object.fromEntries(
              Object.entries(prev).filter(([k]) =>
                bad.some((b) => keyOf(b.employeeId, b.date) === k),
              ),
            ),
          );
          toast[bad.length ? "warning" : "success"](
            `Fixed ${res?.fixed ?? 0} day(s)${bad.length ? `, ${bad.length} failed` : ""}.`,
          );
        },
        onError: (err) =>
          toast.error(getApiErrorMessage(err, "Could not apply the fixes.")),
      },
    );

  const doExport = () =>
    create.mutate(body, {
      onSuccess: (res) => onExported(res),
      onError: (err) => {
        toast.error(getApiErrorMessage(err, "Could not export."));
        check.refetch();
      },
    });

  const nameOf = (id) => {
    const e = withIssues.find((x) => x.employee.id === id)?.employee;
    return e?.name || id;
  };

  return (
    <Dialog
      title="Before export: check the cut-off"
      subtitle={label}
      onClose={onClose}
      busy={busy}
      size="lg"
      footer={
        <>
          <button
            type="button"
            className="btn btn-outline-secondary profile-btn"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          {withIssues.length > 0 && (
            <button
              type="button"
              className="btn btn-outline-primary profile-btn"
              onClick={applyFixes}
              disabled={busy || !draftList.length}
            >
              {resolve.isPending && (
                <span
                  className="spinner-border spinner-border-sm"
                  aria-hidden="true"
                ></span>
              )}
              Apply {draftList.length || ""} fix
              {draftList.length === 1 ? "" : "es"}
            </button>
          )}
          <button
            type="button"
            className="btn btn-primary profile-btn"
            onClick={doExport}
            disabled={busy || check.isFetching || !data?.canExport}
            title={
              data?.canExport
                ? undefined
                : data?.ended === false
                  ? "The cut-off has not ended yet"
                  : withIssues.length
                    ? "Settle or exclude every day listed first"
                    : "Nobody to export"
            }
          >
            {create.isPending && (
              <span
                className="spinner-border spinner-border-sm"
                aria-hidden="true"
              ></span>
            )}
            Export {data?.toExport ?? 0}
          </button>
        </>
      }
    >
      {check.isError ? (
        <div className="alert alert-danger fs-085 py-2 mb-0">
          {getApiErrorMessage(check.error, "Could not check the cut-off.")}
        </div>
      ) : !data ? (
        <div className="text-center py-4">
          <span
            className="spinner-border text-primary"
            role="status"
            aria-label="Checking"
          ></span>
        </div>
      ) : (
        <>
          <div className="d-flex flex-wrap gap-2 mb-3">
            <span className="status-pill status-pill--green">
              To export: {data.toExport}
            </span>
            {data.issueCount > 0 && (
              <span className="status-pill status-pill--amber">
                Days to settle: {data.issueCount}
              </span>
            )}
            {data.excluded > 0 && (
              <span className="status-pill status-pill--gray">
                Excluded: {data.excluded}
              </span>
            )}
            {paidLeave.length + skipped.length > 0 && (
              <button
                type="button"
                className="status-pill status-pill--gray border-0"
                onClick={() => setShowInfo((v) => !v)}
                aria-expanded={showInfo}
              >
                {paidLeave.length > 0 && `Paid leave only: ${paidLeave.length}`}
                {paidLeave.length > 0 && skipped.length > 0 && " · "}
                {skipped.length > 0 && `Skipped: ${skipped.length}`}{" "}
                <i className={`bi bi-chevron-${showInfo ? "up" : "down"}`}></i>
              </button>
            )}
            {data.alreadyExported > 0 && (
              <span className="status-pill status-pill--blue">
                Already processed: {data.alreadyExported}
              </span>
            )}
            {check.isFetching && (
              <span className="fs-075 text-slate-500">
                <span
                  className="spinner-border spinner-border-sm me-1"
                  aria-hidden="true"
                ></span>
                Checking…
              </span>
            )}
          </div>

          {showInfo && (
            <div className="att-card mb-3 fs-080">
              {paidLeave.length > 0 && (
                <>
                  <div className="fw-semibold mb-1">
                    Included for paid leave (no time entries)
                  </div>
                  <ul className="ps-3">
                    {paidLeave.map(({ employee, paidLeaveDays }) => (
                      <li key={employee.id}>
                        {employee.name}{" "}
                        <span className="text-slate-500">
                          {employee.employeeNumber}
                          {employee.branchName
                            ? ` · ${employee.branchName}`
                            : ""}
                          {" · "}
                          {paidLeaveDays} paid leave day(s)
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {skipped.length > 0 && (
                <>
                  <div className="fw-semibold mb-1">Not in this export</div>
                  <ul className="mb-0 ps-3">
                    {skipped.map(({ employee, reason }) => (
                      <li key={employee.id}>
                        {employee.name}
                        <span className="text-slate-500">: {reason}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}

          {excludeIds.length > 0 && (
            <div className="fs-080 mb-3">
              <span className="text-slate-500">Excluded (stay approved): </span>
              {excludeIds.map((id) => (
                <span key={id} className="chip chip--sm me-1">
                  {excluded[id]?.name || id}
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 ms-1"
                    onClick={() => include(id)}
                    disabled={busy}
                  >
                    Include
                  </button>
                </span>
              ))}
            </div>
          )}

          {failed.length > 0 && (
            <div className="alert alert-warning fs-080 py-2">
              <div className="fw-semibold">Not fixed:</div>
              <ul className="mb-0 ps-3">
                {failed.map((f) => (
                  <li key={keyOf(f.employeeId, f.date)}>
                    {nameOf(f.employeeId)}, {formatPlainDate(f.date)}:{" "}
                    {f.error || "failed"}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {data.ended === false && (
            <div className="alert alert-info fs-085 py-2">
              <i className="bi bi-calendar-event me-1"></i>
              This cut-off can be exported after it ends
              {data.period?.processOn
                ? ` (from ${formatPlainDate(data.period.processOn)})`
                : ""}
              . You can settle the days below already.
            </div>
          )}
          {withIssues.length === 0 ? (
            data.toExport > 0 && data.ended !== false ? (
              <div className="alert alert-success fs-085 py-2 mb-0">
                <i className="bi bi-check-circle me-1"></i>
                Everything is settled. <strong>{data.toExport}</strong>{" "}
                employee(s) will be approved, exported and their days locked
                (Processed).
              </div>
            ) : (
              <div className="approval-empty">
                <i className="bi bi-inbox"></i>
                {data.toExport > 0
                  ? `${data.toExport} employee(s) ready once the cut-off ends.`
                  : "Nobody to export: no time entries or paid leave in this cut-off."}
              </div>
            )
          ) : (
            <>
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                <p className="fs-080 text-slate-600 mb-0">
                  These employees have days to settle. Fix the time, mark the
                  day, decide the waiting request, or exclude the employee from
                  this export.
                </p>
                <button
                  type="button"
                  className="btn btn-sm btn-light border"
                  onClick={() => markNoRecordAbsent(withIssues)}
                  disabled={busy}
                >
                  Mark all no-record days absent
                </button>
              </div>
              {incomplete > 0 && (
                <div className="fs-075 text-warning-emphasis mb-2">
                  {incomplete} fix(es) need a time (check-out after check-in).
                </div>
              )}
              {withIssues.map((row) => (
                <div key={row.employee.id} className="att-card mb-2 p-2">
                  <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-1">
                    <div>
                      <span className="fw-semibold">{row.employee.name}</span>{" "}
                      <span className="fs-075 text-slate-500">
                        {row.employee.employeeNumber}
                        {row.employee.branchName
                          ? ` · ${row.employee.branchName}`
                          : ""}
                      </span>
                    </div>
                    <div className="d-flex gap-1">
                      {row.issues.some((i) => i.type === "NO_RECORD") && (
                        <button
                          type="button"
                          className="btn btn-sm btn-light border"
                          onClick={() => markNoRecordAbsent([row])}
                          disabled={busy}
                        >
                          No-record days absent
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => exclude(row.employee)}
                        disabled={busy}
                        title="Leave out of this export; stays approved for a later one"
                      >
                        Exclude
                      </button>
                    </div>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-sm mb-0 fs-080">
                      <tbody>
                        {row.issues.map((issue) => (
                          <IssueRow
                            key={issue.date}
                            group={`fix-${row.employee.id}-${issue.date}`}
                            issue={issue}
                            employee={row.employee}
                            fix={fixes[keyOf(row.employee.id, issue.date)]}
                            onFix={(fix) =>
                              setFix(row.employee.id, issue.date, fix)
                            }
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </>
          )}
        </>
      )}
    </Dialog>
  );
};

export default ExportCheckDialog;
